/**
 * Client of `api/web/statistics/*` (overview, services, geo, operations).
 *
 * Every call unwraps the gateway's `{ data }` envelope and, on failure, throws
 * the structured `toStatsError` result instead of `AxiosManager.stringifyError`'s
 * bare message, so a card can tell a 503 (busy) from a 400 (invalid field).
 *
 * The LIVE/TEST mode is not a param: it travels in the `X-Workmode` header set by
 * `changeMode`, and the response echoes it as `meta.livemode`.
 */
import BaseApi from '../../../../services/common/base-api';
import { toStatsError } from '../../../../utils/statistics/errors';

/** @typedef {import('./types').StatsQuery} StatsQuery */

/**
 * Query params for a statistics route: `tz` is always sent, and `appKey` only
 * when a workflow is selected (the gateway wants a UUID or nothing).
 * @param {StatsQuery} query
 */
export const toStatsParams = ({ from, to, tz, appKey } = {}) => ({
  from,
  to,
  tz,
  ...(appKey ? { appKey } : {}),
});

class StatisticsService extends BaseApi {
  constructor() {
    super(process.env.REACT_APP_ADMIN_API_URL, 'api/web/statistics');
  }

  /**
   * @param {string} endpoint
   * @param {StatsQuery} query
   * @param {{ signal?: AbortSignal }} [options]
   */
  async request(endpoint, query, { signal } = {}) {
    try {
      const {
        data: { data },
      } = await this.axios.get(endpoint, { params: toStatsParams(query), signal });
      return data;
    } catch (err) {
      throw toStatsError(err);
    }
  }

  /** @returns {Promise<import('./types').StatsOverview>} */
  overview(query, options) {
    return this.request('overview', query, options);
  }

  /** @returns {Promise<import('./types').StatsServices>} */
  services(query, options) {
    return this.request('services', query, options);
  }

  /** @returns {Promise<import('./types').StatsGeo>} */
  geo(query, options) {
    return this.request('geo', query, options);
  }

  /** @returns {Promise<import('./types').StatsOperations>} */
  operations(query, options) {
    return this.request('operations', query, options);
  }
}

const statisticsService = new StatisticsService();

export default statisticsService;
